import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Villeneuve-lès-Maguelone' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Villeneuve-lès-Maguelone',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Villeneuve-lès-Maguelone' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Villeneuve-lès-Maguelone. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Les menuiseries proches des étangs à Villeneuve-lès-Maguelone sont-elles plus fragiles ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, l\'humidité et le sel des étangs accélèrent la corrosion des pièces métalliques. Nous utilisons des pièces traitées anticorrosion et des joints renforcés pour les menuiseries exposées. Un entretien régulier des rails et roulettes est recommandé.',
      },
    },
    {
      '@type': 'Question',
      name: 'Intervenez-vous rapidement à Villeneuve-lès-Maguelone ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons sous 24 à 48h, 7j/7, dans tout Villeneuve-lès-Maguelone. Nous couvrons le village, les abords de la cathédrale de Maguelone et les zones résidentielles. Appelez le 09 80 80 22 42.',
      },
    },
  ],
};

export default function VilleneuveLesMaguelonePage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Villeneuve-Maguelone | DFPRO"
        description="Baie coulissante, volet ou vitrage en panne à Villeneuve-lès-Maguelone ? Air salin des étangs : DFPRO intervient 24-48h. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-villeneuve-les-maguelone/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Villeneuve-lès-Maguelone"
        subtitle="Entre étangs et littoral, vos menuiseries subissent sel et humidité. Nos techniciens réparent baies, fenêtres et volets à Villeneuve-lès-Maguelone. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Menuiserie exposée au sel à Villeneuve-lès-Maguelone ?"
        ctaText="Devis gratuit, pièces anticorrosion, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent dans tout Villeneuve-lès-Maguelone."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Villeneuve-lès-Maguelone, entre étangs et littoral, expose ses menuiseries à l'humidité saline. Joints de fenêtre qui ne sont plus étanches, roulettes de baie corrodées, volets qui gonflent : nous utilisons des pièces adaptées. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/villeneuve-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Villeneuve-lès-Maguelone"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : humidité et corrosion</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les maisons proches des étangs subissent une humidité constante qui attaque les <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">roulettes de baie</Link>. La baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link>, frotte, puis refuse de coulisser.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link> avec pièces anticorrosion.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Fenêtre et joints : priorité étanchéité</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              L'humidité des étangs dégrade les <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints de fenêtre</Link>. Une fenêtre qui n'est plus étanche laisse passer l'air humide et augmente la facture énergétique. Nous changeons les joints et réparons <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link> et <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battants</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Volets roulants</Link> : <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">blocage</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>.
            </p>
            <Link to="/reparation-fenetre-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage fenêtre <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/villeneuve-volet-02.jpg"
              alt="Réparation de fenêtre à Villeneuve-lès-Maguelone"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/villeneuve-vitrage-03.webp"
              alt="Remplacement de vitrage à Villeneuve-lès-Maguelone"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrage embué et double vitrage</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">Double vitrage embué</Link> : un classique en zone humide. Nous remplaçons le vitrage sans changer la fenêtre. <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> pour <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">Double vitrage</Link> haute isolation, <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">urgence</Link> 7j/7. Stores : <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">bannes</Link>, <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Villeneuve-lès-Maguelone sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Villeneuve-lès-Maguelone : village, abords de la cathédrale, zones résidentielles. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-saint-jean-de-vedas/" className="text-accent-500 font-semibold hover:underline">Saint-Jean-de-Védas</Link>, <Link to="/depannage-menuiserie-frontignan/" className="text-accent-500 font-semibold hover:underline">Frontignan</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}